import { capitalize } from '@mui/material';
import LinkifyIt from 'linkify-it';
import {
  ContentSchema,
  RoomJSONStructureContent,
} from '../../definitions/schemas';
import ContentType from '../../enums/ContentType';
import { isVendorMode } from '../../lib/Vendor';

export const getContentTitle = (
  content: Partial<ContentSchema>,
  fallbackToText = false
): string => {
  const { name, text } = content;

  let title = `${name}`;
  let isUrl = false;

  if (text?.text) {
    [, isUrl] = getTextContentURL(text.text);

    if (fallbackToText) {
      title = text.text;
    }
  }

  return isUrl ? title : capitalize(title);
};

export const getContentType = (content: Partial<ContentSchema>): string => {
  const { type } = content;

  switch (type) {
    case ContentType.DOCUMENT:
      return 'DOC';
    case ContentType.EMBEDDABLE:
      return 'EMBED';
    case ContentType.TEXT: {
      const [, isUrl] = getTextContentURL(content.text?.text);
      return isUrl ? 'LINK' : 'TEXT';
    }
    default:
      return type?.toUpperCase() || '';
  }
};

/**
 * Processes a text content to determine if it contains a URL and if the
 * text is a URL itself, without any other text that isn't part of a URL.
 * This is useful to distinguish the content with 'text' type from
 * actual links/URLs that are submitted by users.
 */
export const getTextContentURL = (text = ''): [string, boolean] => {
  const matches = new LinkifyIt().match(text) || [];
  const match = matches[0];

  if (!match) {
    return ['', false];
  }

  const isUrl = match.index === 0 && match.lastIndex === text.length;
  return [match.url, isUrl];
};

/**
 * Gets a URL to use for contents' covers, whether a lazy signed cover OR
 * a signed cover URL. The same for a default cover, which could be lazy
 * signed or pre-signed.
 */
export const getContentCover = (
  content: Partial<ContentSchema>,
  structureContent?: RoomJSONStructureContent
): [string | undefined, string | undefined, string | undefined] => {
  const { cover, defaultCover, signedCover, signedDefaultCover } = content;
  const coverUrl = signedCover ?? cover;
  const defaultCoverUrl = signedDefaultCover ?? defaultCover;
  const coverFromStructure = getContentCoverFromStructure(structureContent);

  return [coverUrl, defaultCoverUrl, coverFromStructure];
};

export const hasCover = (
  content: Partial<ContentSchema>,
  structureContent?: RoomJSONStructureContent
): boolean => {
  const [coverUrl, defaultCoverUrl, coverFromStructure] = getContentCover(
    content,
    structureContent
  );
  return !!(coverFromStructure || coverUrl || defaultCoverUrl);
};

export const getContentCoverFromStructure = (
  structureContent?: RoomJSONStructureContent
): string | undefined => {
  if (!structureContent || !isVendorMode()) {
    return;
  }

  const { coverSigned } = structureContent;
  return coverSigned;
};

export const getCallAiRecordingId = (url: string): string => {
  const parsedUrl = new URL(url);
  const pathSegments = parsedUrl.pathname.split('/');
  return pathSegments[pathSegments.length - 1];
};
